import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navbar } from 'components/Navbar';
import { ViewContent } from 'components/ViewContent';
import { modelsListType } from 'modules/Listings/store/Store.types';
import { NavTabs } from 'modules/Featured/components/NavTabs/NavTabs';
import { useMeasure, useIntersection } from 'react-use';
import { cx } from '@linaria/core';
import dynamic from 'next/dynamic';
import { ModelTypeIcon } from 'components/ModelTypeIcon';
import { breakpoints, flex } from 'styles/utils';
import { useCurrentStore } from 'modules/Listings/store/useCurrentStore';
import { observer } from 'mobx-react-lite';
import { IconCloseRound } from 'components/Icons';
import { isQuickFilters } from 'modules/Models/components/QuickTypesFilter/constant';
import { JiraButton, JiraLinks } from 'components/Jira';
import { useIsLoggedIn } from 'modules/Auth/hooks';
import { useBreakpoint } from 'utils/responsive';
import { extraPadding, navTabsPadding } from 'modules/User/styles';
import {
  headerWrapper,
  listWrapper,
  filterWrapper,
  contentWrapper,
  container,
  widthWithFilters,
  widthWithoutFilters,
  headerWithFilters,
  headerWithoutFilters,
  stickyHeaderClass,
  withStickyClass,
  listHeaderWrapper,
  listFilterWrapper,
  listHeaderTitle,
  listHeaderSubtitle,
  filtersValuesContainer,
  filtersValuesWrapper,
  filterValueContainer,
  filterValueTitle,
  filterValueCloseIcon,
  beforeFilterWrapper,
  afterFilterWrapper,
  minHeight100,
  filtersTitle,
  filtersValuesVisible,
  filtersValuesHidden,
  requestLink,
  requestLinkWrapper,
} from './withListLayout.styles';
import { ListHeading } from './components/ListHeading/ListHeading';
import { SidebarFilterProps } from './components/FilterArea';

const FilterAreaDynamic = dynamic<SidebarFilterProps>(() => import('modules/Listings/components/FilterArea').then((mod) => mod.FilterArea), {
  ssr: false,
});

export function withListLayout<T = Record<string, unknown>>(
  Component: React.FC<T>,
  withFilters = true,
): (props: T & { title?: string; heading?: string; requestLinkText?: string }) => JSX.Element {
  return observer((props: T & { title?: string; heading?: string; requestLinkText?: string }): JSX.Element => {
    const { title, heading, requestLinkText, ...restProps } = props;
    const compProps = restProps as T;
    const [measureRef, { width, height }] = useMeasure();
    const mRefTyped = measureRef as React.LegacyRef<HTMLDivElement>;

    const [measureListRef, { height: filterHeight }] = useMeasure();
    const mListRefTyped = measureListRef as React.LegacyRef<HTMLDivElement>;

    const [contentWrapperRef, leftPosition] = useFilterStickyPosition(width);

    const ref = useRef<HTMLDivElement>(null);
    const intersection = useIntersection(ref, { rootMargin: '60px' });

    const filterIntersectRef = useRef<HTMLDivElement>(null);
    const filterIntersection = useIntersection(filterIntersectRef, { rootMargin: '-100px', threshold: 0.9 });

    const beforeFilterIntersectRef = useRef<HTMLDivElement>(null);
    const beforeFilterIntersection = useIntersection(beforeFilterIntersectRef, { rootMargin: '-163px' });
    const isLoggedIn = useIsLoggedIn();
    const isDesktopScreen = useBreakpoint(breakpoints.up('tab'));

    useEffect(() => {
      const el = document.querySelector('#topHeaderElement');
      // eslint-disable-next-line unicorn/prefer-query-selector
      const listFilterElement = document.getElementById('listFilterElement');
      // eslint-disable-next-line unicorn/prefer-query-selector
      const afterListFilterElement = document.getElementById('afterListFilterElement');

      if (width < 1024) {
        if (beforeFilterIntersection && beforeFilterIntersection.isIntersecting) {
          if (listFilterElement) {
            listFilterElement.classList.remove('sticky_filter');
            listFilterElement.style.zIndex = '10001';
          }
          el?.classList.add('sticky_shadow');
          if (afterListFilterElement) {
            afterListFilterElement.style.paddingTop = '0px';
          }
        } else {
          if (listFilterElement) {
            listFilterElement.classList.add('sticky_filter');
            listFilterElement.style.zIndex = '9999';
          }
          el?.classList.remove('sticky_shadow');
          if (afterListFilterElement) {
            afterListFilterElement.style.paddingTop = `${filterHeight}px`;
          }
        }
      } else {
        listFilterElement?.classList.remove('sticky_filter');
        if (afterListFilterElement) {
          afterListFilterElement.style.paddingTop = '0px';
        }
      }
    }, [beforeFilterIntersection, width, filterHeight]);

    const [showFiltersValues, setShowFiltersValues] = useState(false);
    useEffect(() => {
      const isNoRecords = document.querySelector('div[data-testid="default-empty-apps-ui"]');
      if (width >= 1024 && !isNoRecords) {
        const rightFilter = document.querySelector('#rightFilterElement');
        if (filterIntersection && filterIntersection.isIntersecting) {
          setShowFiltersValues(false);
          rightFilter?.classList.remove('sticky_right');
        } else {
          setShowFiltersValues(true);
          rightFilter?.classList.add('sticky_right');
        }
      } else {
        setShowFiltersValues(false);
      }
    }, [filterIntersection, width]);

    useEffect(() => {
      if (isDesktopScreen) {
        const el = document.querySelector('#topHeaderElement');

        if (intersection && intersection.isIntersecting) {
          el?.classList.remove('sticky');
          el?.classList.remove('sticky_shadow');
        } else {
          el?.classList.add('sticky');
          el?.classList.add('sticky_shadow');
        }
      }
    }, [intersection]);

    const store = useCurrentStore();
    const showFilter = store.showFilter;

    const storeFilters = store.filters.filter(({ value }) => value.length);

    // const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
    useEffect(() => {
      if (width && height) {
        if (width < 1024) {
          store.setShowFilter(false);
        } else {
          store.setShowFilter(true);
        }
      }
    }, [width, height]);

    const FilterValues =
      storeFilters.length > 0 ? (
        <>
          <div className={filtersTitle}>Filters:</div>
          {storeFilters.map((filter) => {
            return filter.value.map((v) => {
              const isModelType = filter.field === 'model_type_id' || isQuickFilters(filter.field, v);
              return isModelType ? (
                <ModelTypeIcon
                  onClose={(value: string) => (store as modelsListType).makeSetFilter(filter.field)(filter.value.filter((a) => a !== value))}
                  modelTypeId={v}
                  modelTypeName={v}
                />
              ) : (
                <div className={filterValueContainer}>
                  <div className={filterValueTitle}>{v}</div>
                  <IconCloseRound
                    size={10}
                    color="#0046D4"
                    className={filterValueCloseIcon}
                    onClick={() => {
                      (store as modelsListType).makeSetFilter(filter.field)(filter.value.filter((a) => a !== v));
                    }}
                  />
                </div>
              );
            });
          })}
        </>
      ) : null;

    return (
      <div ref={mRefTyped} className={cx(container, withStickyClass)}>
        <div ref={ref}>
          <Navbar />
        </div>
        <div className={cx('bound-to-max')}>
          <div className={cx(headerWrapper, withFilters ? headerWithFilters : headerWithoutFilters, navTabsPadding)}>
            <NavTabs className={flex.flex.one} data-testid="navTabsForList" />
          </div>
        </div>
        <div id="topHeaderElement" className={cx(stickyHeaderClass)}>
          <div className={cx('bound-to-max', headerWrapper, withFilters ? headerWithFilters : headerWithoutFilters)}>
            <ListHeading />
          </div>
          <div className={cx('bound-to-max', filtersValuesContainer, showFiltersValues ? filtersValuesVisible : filtersValuesHidden)}>
            <div className={filtersValuesWrapper}>{showFiltersValues && FilterValues}</div>
          </div>
        </div>
        <div className="splitter" />
        <ViewContent className={cx(minHeight100, withFilters ? undefined : undefined)}>
          <main className={cx('bound-to-max', 'fullHeight', extraPadding, flex.init, flex.direction.column)}>
            {title ? (
              <div className={listHeaderWrapper}>
                <h1 className={listHeaderTitle}>{title}</h1>
                <div className={requestLinkWrapper}>
                  {heading && <h2 className={listHeaderSubtitle}>{heading}</h2>}
                  {isLoggedIn && requestLinkText && (
                    <JiraButton collectorId={JiraLinks.sendFeedback.collectorId} clickTargetSelector={JiraLinks.sendFeedback.clickTargetSelector}>
                      <button type="button" className={requestLink}>
                        {requestLinkText}
                      </button>
                    </JiraButton>
                  )}
                </div>
              </div>
            ) : null}

            <div ref={contentWrapperRef} className={cx(contentWrapper)}>
              <div className={cx(listWrapper, withFilters ? widthWithFilters : widthWithoutFilters)}>
                <div className={beforeFilterWrapper} ref={beforeFilterIntersectRef} />
                <div className={listFilterWrapper} id="listFilterElement" ref={mListRefTyped}>
                  {FilterValues}
                </div>
                <div className={afterFilterWrapper} id="afterListFilterElement" ref={filterIntersectRef} />
                <Component {...compProps} parentWidth={width} />
              </div>
              {withFilters ? (
                <div id="rightFilterElement" className={filterWrapper} style={{ display: showFilter ? 'flex' : 'none', left: leftPosition }}>
                  <FilterAreaDynamic />
                </div>
              ) : null}
            </div>
          </main>
        </ViewContent>
      </div>
    );
  });
}

function useFilterStickyPosition(pageWidth: number): [React.RefObject<HTMLDivElement>, number | undefined] {
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line consistent-return
  const leftPosition = useMemo(() => {
    if (contentWrapperRef.current && pageWidth >= 1024) {
      // incase of right - contentWrapperRef.current.getBoundingClientRect().right - FILTER_WIDTH_IN_NUMBER;
      return contentWrapperRef.current.getBoundingClientRect().left;
    }
  }, [contentWrapperRef.current, pageWidth]);

  return [contentWrapperRef, leftPosition];
}
